import { useMemo, useState } from 'react';
import {
  Box, Stack, Typography, Card, Alert, Tabs, Tab,
  TextField, InputAdornment, Chip, IconButton, Tooltip,
} from '@mui/material';
import {
  DataGrid,
  type GridColDef,
  type GridRenderCellParams,
} from '@mui/x-data-grid';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';

import { useAuth } from '../../context/AuthContext';
import { usePagos } from '../../hooks/usePagos';
import { formatCurrency, formatDate } from '../../utils/format';
import {
  type Pago,
  type PagoEstado,
  getEstadoPagoLabel,
  getEstadoPagoColor,
  getViaPagoLabel,
} from '../../types/pago';
import AccionPagoDialog from './AccionPagoDialog';

type TabValue = 'pendientes' | 'todos';

export default function PagosList() {
  const { effectiveRole, effectiveEmpresaId } = useAuth();
  const isAdminEmpresa = effectiveRole === 'admin_empresa';
  const puedeOperar = effectiveRole === 'admin_general' || effectiveRole === 'operador';

  const [tab, setTab] = useState<TabValue>(puedeOperar ? 'pendientes' : 'todos');
  const [busqueda, setBusqueda] = useState('');
  const [accion, setAccion] = useState<{
    pago: Pago;
    modo: 'ver' | 'confirmar' | 'rechazar';
  } | null>(null);

  const filtros = useMemo(
    () => ({
      empresaId: isAdminEmpresa ? (effectiveEmpresaId ?? undefined) : undefined,
      estado: tab === 'pendientes' ? ('pendiente' as PagoEstado) : undefined,
    }),
    [isAdminEmpresa, effectiveEmpresaId, tab]
  );

  const { data: pagos = [], isError, isLoading } = usePagos(filtros);

  // ── Filtro cliente (búsqueda por texto) ───────────────────────────────────

  const filtered = useMemo(() => {
    if (!busqueda.trim()) return pagos;
    const q = busqueda.toLowerCase();
    return pagos.filter((p) =>
      p.empresa?.razon_social?.toLowerCase().includes(q) ||
      p.cupon?.codigo?.toLowerCase().includes(q) ||
      p.periodo?.periodo?.toLowerCase().includes(q) ||
      p.nro_comprobante?.toLowerCase().includes(q)
    );
  }, [pagos, busqueda]);

  // ── Columns ───────────────────────────────────────────────────────────────

  const columns: GridColDef<Pago>[] = [
    ...(!isAdminEmpresa
      ? [{
          field: 'empresa',
          headerName: 'Empresa',
          flex: 1.4,
          minWidth: 180,
          renderCell: (p: GridRenderCellParams<Pago>) =>
            p.row.empresa?.razon_social ?? '-',
        } as GridColDef<Pago>]
      : []),
    {
      field: 'cupon',
      headerName: 'Cupón',
      width: 220,
      renderCell: (p: GridRenderCellParams<Pago>) => (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <ReceiptLongIcon fontSize="small" color="action" />
          <Typography variant="body2" fontFamily="monospace" fontSize={12}>
            {p.row.cupon?.codigo ?? '—'}
          </Typography>
        </Stack>
      ),
    },
    {
      field: 'via_pago',
      headerName: 'Vía',
      width: 160,
      renderCell: (p: GridRenderCellParams<Pago>) =>
        getViaPagoLabel(p.row.via_pago),
    },
    {
      field: 'monto_pagado',
      headerName: 'Monto pagado',
      width: 140,
      align: 'right',
      headerAlign: 'right',
      renderCell: (p: GridRenderCellParams<Pago>) => (
        <Typography variant="body2" fontWeight={500}>
          {formatCurrency(p.row.monto_pagado)}
        </Typography>
      ),
    },
    {
      field: 'estado',
      headerName: 'Estado',
      width: 130,
      renderCell: (p: GridRenderCellParams<Pago>) => (
        <Chip
          label={getEstadoPagoLabel(p.row.estado)}
          color={getEstadoPagoColor(p.row.estado)}
          size="small"
        />
      ),
    },
    {
      field: 'fecha_pago',
      headerName: 'Fecha pago',
      width: 110,
      renderCell: (p: GridRenderCellParams<Pago>) => formatDate(p.row.fecha_pago),
    },
    {
      field: 'acciones',
      headerName: 'Acciones',
      width: 140,
      sortable: false,
      filterable: false,
      renderCell: (p: GridRenderCellParams<Pago>) => {
        const esPendiente = p.row.estado === 'pendiente' || p.row.estado === 'pre_confirmado';
        return (
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="Ver detalle">
              <IconButton size="small" onClick={() => setAccion({ pago: p.row, modo: 'ver' })}>
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {puedeOperar && esPendiente && (
              <>
                <Tooltip title="Confirmar acreditación">
                  <IconButton
                    size="small"
                    color="success"
                    onClick={() => setAccion({ pago: p.row, modo: 'confirmar' })}
                  >
                    <CheckCircleIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Rechazar">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => setAccion({ pago: p.row, modo: 'rechazar' })}
                  >
                    <CancelIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Stack>
        );
      },
    },
  ];

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" fontWeight={600}>
          Pagos
        </Typography>
      </Stack>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v as TabValue)}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab value="pendientes" label="Pendientes de confirmación" />
        <Tab value="todos" label="Todos" />
      </Tabs>

      <TextField
        size="small"
        placeholder="Buscar por empresa, cupón, comprobante…"
        value={busqueda}
        onChange={(e) => setBusqueda(e.target.value)}
        sx={{ mb: 2, width: 360 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />

      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Error al cargar los pagos.
        </Alert>
      )}

      <Card>
        <DataGrid
          rows={filtered}
          columns={columns}
          loading={isLoading}
          autoHeight
          disableRowSelectionOnClick
          pageSizeOptions={[25, 50, 100]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          sx={{ border: 0 }}
          localeText={{ noRowsLabel: 'Sin pagos para mostrar' }}
        />
      </Card>

      {accion && (
        <AccionPagoDialog
          open
          pago={accion.pago}
          modo={accion.modo}
          onClose={() => setAccion(null)}
        />
      )}
    </Box>
  );
}
